import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Domain {
  id: number;
  domain: string;
  verification_status: string;
  dkim_enabled: boolean;
  mail_from_domain: string;
  default_from_email: string;
  bounce_rate: number;
  complaint_rate: number;
  created_at: string;
  dnsRecords?: DNSRecord[];
  allRecordsVerified?: boolean;
}

interface DNSRecord {
  record_type: string;
  record_name: string;
  record_value: string;
  ttl: number;
  priority?: number;
  is_verified: boolean;
  instruction?: string;
  status?: string;
}

interface ActivityLogEntry {
  id: string;
  timestamp: Date;
  action: string;
  type: 'request' | 'response' | 'error' | 'success' | 'info';
  details: any;
  domainId?: number;
  domainName?: string;
  duration?: number;
}

interface ExpandedDetails {
  [key: string]: boolean;
}

const DomainManagerEnhanced: React.FC = () => {
  const [domains, setDomains] = useState<Domain[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null);
  const [dnsRecords, setDnsRecords] = useState<DNSRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'add' | 'manage' | 'verify' | 'activity'>('add');
  const [verificationStatus, setVerificationStatus] = useState<Record<number, string>>({});
  const [copiedRecord, setCopiedRecord] = useState<string | null>(null);
  const [activityLog, setActivityLog] = useState<ActivityLogEntry[]>([]);
  const [expandedDetails, setExpandedDetails] = useState<ExpandedDetails>({});
  const [domainForm, setDomainForm] = useState({
    domain: '',
    enableMailFrom: true,
    defaultFromEmail: '',
    errors: {
      domain: '',
      defaultFromEmail: ''
    }
  });

  // Add activity log entry
  const addLogEntry = useCallback((
    action: string,
    type: ActivityLogEntry['type'],
    details: any,
    domainId?: number,
    domainName?: string,
    duration?: number
  ) => {
    const entry: ActivityLogEntry = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      timestamp: new Date(),
      action,
      type,
      details,
      domainId,
      domainName,
      duration
    };

    setActivityLog(prev => [entry, ...prev.slice(0, 49)]); // Keep last 50 entries
  }, []);

  // API call wrapper with logging
  const apiCall = useCallback(async (
    url: string,
    options: RequestInit = {},
    action: string,
    domainId?: number,
    domainName?: string
  ) => {
    const startTime = Date.now();

    addLogEntry(
      `${action} - Request sent`,
      'request',
      { url, method: options.method || 'GET', body: options.body },
      domainId,
      domainName
    );

    try {
      const response = await fetch(url, options);
      const data = await response.json();
      const duration = Date.now() - startTime;

      if (response.ok && data.success) {
        addLogEntry(
          `${action} - Success`,
          'success',
          { status: response.status, data },
          domainId,
          domainName,
          duration
        );
      } else {
        addLogEntry(
          `${action} - API Error`,
          'error',
          { status: response.status, error: data.error || 'Unknown error', data },
          domainId,
          domainName,
          duration
        );
      }

      return { response, data, duration };
    } catch (error) {
      const duration = Date.now() - startTime;
      addLogEntry(
        `${action} - Network Error`,
        'error',
        { error: error instanceof Error ? error.message : 'Network error' },
        domainId,
        domainName,
        duration
      );
      throw error;
    }
  }, [addLogEntry]);

  useEffect(() => {
    fetchDomains();
  }, []);

  const validateDomain = (domain: string): string => {
    if (!domain) return 'Domain is required';
    if (!/^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.[a-zA-Z]{2,}$/.test(domain)) {
      return 'Please enter a valid domain (e.g., example.com)';
    }
    return '';
  };

  const validateEmail = (email: string): string => {
    if (!email) return '';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const fetchDomains = async () => {
    setLoading(true);
    try {
      const { data } = await apiCall(
        'http://localhost:8787/api/domains?userId=1',
        {},
        'Fetch Domains'
      );

      if (data.success) {
        setDomains(data.domains);
        addLogEntry(
          `Loaded ${data.domains.length} domains`,
          'info',
          { count: data.domains.length, domains: data.domains.map((d: Domain) => d.domain) }
        );
      }
    } catch (error) {
      console.error('Failed to fetch domains:', error);
    } finally {
      setLoading(false);
    }
  };

  const addDomain = async () => {
    const domainError = validateDomain(domainForm.domain);
    const emailError = validateEmail(domainForm.defaultFromEmail);

    setDomainForm(prev => ({
      ...prev,
      errors: { domain: domainError, defaultFromEmail: emailError }
    }));

    if (domainError || emailError) return;

    setLoading(true);
    const cleanDomain = domainForm.domain.toLowerCase().trim();

    try {
      const { data } = await apiCall(
        'http://localhost:8787/api/domains',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            domain: cleanDomain,
            enableMailFrom: domainForm.enableMailFrom,
            defaultFromEmail: domainForm.defaultFromEmail || `noreply@${cleanDomain}`,
            userId: 1
          })
        },
        'Add Domain',
        undefined,
        cleanDomain
      );

      if (data.success) {
        setDnsRecords(data.dnsRecords);
        await fetchDomains();
        setActiveTab('verify');
        setDomainForm({
          domain: '',
          enableMailFrom: true,
          defaultFromEmail: '',
          errors: { domain: '', defaultFromEmail: '' }
        });

        // Find the newly added domain and select it
        const newDomainObj = domains.find(d => d.domain === cleanDomain);
        if (newDomainObj) {
          setSelectedDomain(newDomainObj);
        }

        addLogEntry(
          `Domain ${cleanDomain} added successfully`,
          'success',
          {
            domain: cleanDomain,
            dnsRecordsCount: data.dnsRecords?.length || 0,
            dnsRecords: data.dnsRecords
          },
          undefined,
          cleanDomain
        );
      }
    } catch (error) {
      console.error('Error adding domain:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDNSRecords = async (domainId: number, domainName: string) => {
    try {
      const { data } = await apiCall(
        `http://localhost:8787/api/domains/${domainId}/dns`,
        {},
        'Fetch DNS Records',
        domainId,
        domainName
      );

      if (data.success) {
        setDnsRecords(data.records);
        addLogEntry(
          `Loaded ${data.records.length} DNS records for ${domainName}`,
          'info',
          { records: data.records },
          domainId,
          domainName
        );
      }
    } catch (error) {
      console.error('Failed to fetch DNS records:', error);
    }
  };

  const verifyDomain = async (domainId: number, domainName: string) => {
    setVerificationStatus({ ...verificationStatus, [domainId]: 'checking' });

    try {
      const { data } = await apiCall(
        `http://localhost:8787/api/domains/${domainId}/verify`,
        { method: 'POST' },
        'Verify Domain',
        domainId,
        domainName
      );

      if (data.success) {
        setVerificationStatus({ ...verificationStatus, [domainId]: 'verified' });
        await fetchDomains();
        addLogEntry(
          `Domain ${domainName} verified successfully`,
          'success',
          { verification: data },
          domainId,
          domainName
        );
      } else {
        setVerificationStatus({ ...verificationStatus, [domainId]: 'pending' });
        addLogEntry(
          `Domain ${domainName} verification failed`,
          'error',
          { message: data.message, verification: data },
          domainId,
          domainName
        );
      }
    } catch (error) {
      setVerificationStatus({ ...verificationStatus, [domainId]: 'error' });
      console.error('Failed to verify domain:', error);
    }
  };

  const copyToClipboard = async (text: string, recordId: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedRecord(recordId);
      setTimeout(() => setCopiedRecord(null), 2000);

      addLogEntry(
        `Copied ${label} to clipboard`,
        'info',
        { text, label },
        selectedDomain?.id,
        selectedDomain?.domain
      );
    } catch (error) {
      addLogEntry(
        `Failed to copy ${label}`,
        'error',
        { error: error instanceof Error ? error.message : 'Copy failed' },
        selectedDomain?.id,
        selectedDomain?.domain
      );
    }
  };

  const toggleExpandedDetails = (entryId: string) => {
    setExpandedDetails(prev => ({
      ...prev,
      [entryId]: !prev[entryId]
    }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'verified': return 'text-green-400';
      case 'pending': return 'text-yellow-400';
      case 'failed': return 'text-red-400';
      case 'checking': return 'text-blue-400';
      default: return 'text-gray-400';
    }
  };

  const getLogTypeColor = (type: ActivityLogEntry['type']) => {
    switch (type) {
      case 'success': return 'bg-green-500/20 border-green-500 text-green-400';
      case 'error': return 'bg-red-500/20 border-red-500 text-red-400';
      case 'request': return 'bg-blue-500/20 border-blue-500 text-blue-400';
      case 'response': return 'bg-purple-500/20 border-purple-500 text-purple-400';
      case 'info': return 'bg-gray-500/20 border-gray-500 text-gray-400';
      default: return 'bg-gray-500/20 border-gray-500 text-gray-400';
    }
  };

  const getRecordTypeColor = (type: string) => {
    switch (type) {
      case 'TXT': return 'bg-blue-500';
      case 'CNAME': return 'bg-purple-500';
      case 'MX': return 'bg-green-500';
      case 'SPF': return 'bg-orange-500';
      case 'DKIM': return 'bg-pink-500';
      case 'DMARC': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const formatTimestamp = (timestamp: Date) => {
    return timestamp.toLocaleTimeString() + '.' + timestamp.getMilliseconds().toString().padStart(3, '0');
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
            Enhanced Domain Management Center
          </h1>
          <p className="text-gray-400">Advanced domain management with real-time activity monitoring</p>

          {/* Activity Summary */}
          <div className="mt-4 flex gap-4 text-sm">
            <div className="bg-gray-800 px-3 py-1 rounded-full">
              <span className="text-gray-400">Domains: </span>
              <span className="text-white font-bold">{domains.length}</span>
            </div>
            <div className="bg-gray-800 px-3 py-1 rounded-full">
              <span className="text-gray-400">Activity Log: </span>
              <span className="text-white font-bold">{activityLog.length}</span>
            </div>
            <div className="bg-gray-800 px-3 py-1 rounded-full">
              <span className="text-gray-400">Verified: </span>
              <span className="text-green-400 font-bold">
                {domains.filter(d => d.verification_status === 'verified').length}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          {[
            { id: 'add', label: 'Add Domain', icon: '➕' },
            { id: 'manage', label: 'Manage Domains', icon: '📊' },
            { id: 'verify', label: 'Verify DNS', icon: '✅' },
            { id: 'activity', label: 'Activity Log', icon: '📋' }
          ].map((tab) => (
            <motion.button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-4 py-2 rounded-lg font-bold transition ${
                activeTab === tab.id ? 'bg-blue-600' : 'bg-gray-800 hover:bg-gray-700'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {tab.icon} {tab.label}
            </motion.button>
          ))}
        </div>

        {/* Add Domain Tab */}
        {activeTab === 'add' && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-gray-800 rounded-lg p-6"
          >
            <h2 className="text-xl font-bold mb-4">Add New Domain</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Domain Name *</label>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={domainForm.domain}
                      onChange={(e) => setDomainForm(prev => ({
                        ...prev,
                        domain: e.target.value,
                        errors: { ...prev.errors, domain: '' }
                      }))}
                      placeholder="example.com"
                      className={`w-full px-4 py-2 bg-gray-900 rounded border ${
                        domainForm.errors.domain ? 'border-red-500' : 'border-gray-700'
                      } text-white focus:border-blue-500 focus:outline-none`}
                    />
                    {domainForm.errors.domain && (
                      <p className="text-red-400 text-xs mt-1">{domainForm.errors.domain}</p>
                    )}
                  </div>
                  <motion.button
                    onClick={addDomain}
                    disabled={!domainForm.domain || loading}
                    className="px-6 py-2 bg-blue-600 rounded font-bold hover:bg-blue-700 disabled:opacity-50"
                    whileHover={!loading ? { scale: 1.02 } : {}}
                    whileTap={!loading ? { scale: 0.98 } : {}}
                  >
                    {loading ? (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                      />
                    ) : (
                      'Add Domain'
                    )}
                  </motion.button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Default From Email (optional)</label>
                <input
                  type="email"
                  value={domainForm.defaultFromEmail}
                  onChange={(e) => setDomainForm(prev => ({
                    ...prev,
                    defaultFromEmail: e.target.value,
                    errors: { ...prev.errors, defaultFromEmail: '' }
                  }))}
                  placeholder={domainForm.domain ? `noreply@${domainForm.domain}` : 'noreply@example.com'}
                  className={`w-full px-4 py-2 bg-gray-900 rounded border ${
                    domainForm.errors.defaultFromEmail ? 'border-red-500' : 'border-gray-700'
                  } text-white focus:border-blue-500 focus:outline-none`}
                />
                {domainForm.errors.defaultFromEmail && (
                  <p className="text-red-400 text-xs mt-1">{domainForm.errors.defaultFromEmail}</p>
                )}
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="enableMailFrom"
                  checked={domainForm.enableMailFrom}
                  onChange={(e) => setDomainForm(prev => ({
                    ...prev,
                    enableMailFrom: e.target.checked
                  }))}
                  className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-700 rounded focus:ring-blue-500"
                />
                <label htmlFor="enableMailFrom" className="text-sm font-medium">
                  Enable MAIL FROM domain (recommended for deliverability)
                </label>
              </div>

              <div className="bg-blue-900/20 border border-blue-600 rounded-lg p-4">
                <h3 className="font-bold text-blue-400 mb-2">📝 What happens when you add a domain:</h3>
                <ol className="list-decimal list-inside space-y-1 text-sm">
                  <li>We generate verification tokens and DKIM keys</li>
                  <li>We provide DNS records for you to add</li>
                  <li>You add these records to your DNS provider</li>
                  <li>We verify the records and activate the domain</li>
                  <li>You can start sending emails from this domain</li>
                </ol>
              </div>
            </div>
          </motion.div>
        )}

        {/* Manage Domains Tab */}
        {activeTab === 'manage' && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            <h2 className="text-xl font-bold mb-4">Your Domains</h2>

            {domains.length === 0 ? (
              <div className="bg-gray-800 rounded-lg p-8 text-center">
                <p className="text-gray-400 mb-4">No domains added yet</p>
                <button
                  onClick={() => setActiveTab('add')}
                  className="px-4 py-2 bg-blue-600 rounded font-bold hover:bg-blue-700"
                >
                  Add Your First Domain
                </button>
              </div>
            ) : (
              <AnimatePresence>
                {domains.map((domain, index) => (
                  <motion.div
                    key={domain.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-gray-800 rounded-lg p-4 hover:bg-gray-750 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-bold">{domain.domain}</h3>
                        <div className="flex gap-4 mt-2 text-sm">
                          <span className={`font-medium ${getStatusColor(domain.verification_status)}`}>
                            Status: {domain.verification_status}
                          </span>
                          <span className="text-gray-400">
                            DKIM: {domain.dkim_enabled ? '✅' : '❌'}
                          </span>
                          <span className="text-gray-400">
                            Bounce: {domain.bounce_rate}%
                          </span>
                          <span className="text-gray-400">
                            Complaint: {domain.complaint_rate}%
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Default FROM: {domain.default_from_email}
                        </p>
                        <p className="text-xs text-gray-500">
                          Added: {new Date(domain.created_at).toLocaleDateString()}
                        </p>
                      </div>

                      <div className="flex gap-2">
                        <motion.button
                          onClick={() => {
                            setSelectedDomain(domain);
                            fetchDNSRecords(domain.id, domain.domain);
                            setActiveTab('verify');
                          }}
                          className="px-3 py-1 bg-blue-600 rounded text-sm font-bold hover:bg-blue-700"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          View DNS
                        </motion.button>
                        {domain.verification_status !== 'verified' && (
                          <motion.button
                            onClick={() => verifyDomain(domain.id, domain.domain)}
                            disabled={verificationStatus[domain.id] === 'checking'}
                            className="px-3 py-1 bg-green-600 rounded text-sm font-bold hover:bg-green-700 disabled:opacity-50"
                            whileHover={verificationStatus[domain.id] !== 'checking' ? { scale: 1.05 } : {}}
                            whileTap={verificationStatus[domain.id] !== 'checking' ? { scale: 0.95 } : {}}
                          >
                            {verificationStatus[domain.id] === 'checking' ? (
                              <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                              />
                            ) : (
                              'Verify'
                            )}
                          </motion.button>
                        )}
                      </div>
                    </div>

                    {domain.allRecordsVerified && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="mt-3 p-2 bg-green-900/20 border border-green-600 rounded"
                      >
                        <p className="text-sm text-green-400">
                          ✅ All DNS records verified - Ready to send emails!
                        </p>
                      </motion.div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            )}
          </motion.div>
        )}

        {/* Verify DNS Tab */}
        {activeTab === 'verify' && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            <h2 className="text-xl font-bold mb-4">DNS Records Setup</h2>

            {selectedDomain ? (
              <>
                <div className="bg-gray-800 rounded-lg p-4 mb-4">
                  <h3 className="text-lg font-bold">{selectedDomain.domain}</h3>
                  <p className="text-sm text-gray-400">
                    Add these DNS records to your domain provider
                  </p>
                </div>

                <AnimatePresence>
                  {dnsRecords.map((record, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-gray-800 rounded-lg p-4"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-1 rounded text-xs font-bold text-white ${getRecordTypeColor(record.record_type)}`}>
                            {record.record_type}
                          </span>
                          <span className="text-sm font-medium">
                            {record.status || (record.is_verified ? '✅ Verified' : '⏳ Pending')}
                          </span>
                        </div>
                        <motion.button
                          onClick={() => copyToClipboard(record.record_value, `${record.record_type}-${index}`, `${record.record_type} record value`)}
                          className="px-3 py-1 bg-gray-700 rounded text-sm hover:bg-gray-600"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          {copiedRecord === `${record.record_type}-${index}` ? '✅ Copied!' : '📋 Copy Value'}
                        </motion.button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <label className="text-gray-400">Name/Host:</label>
                            <motion.button
                              onClick={() => copyToClipboard(record.record_name, `name-${index}`, 'record name')}
                              className="text-xs px-2 py-0.5 bg-gray-700 rounded hover:bg-gray-600"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              Copy
                            </motion.button>
                          </div>
                          <code className="block bg-black/50 p-2 rounded break-all">
                            {record.record_name}
                          </code>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <label className="text-gray-400">Value/Points to:</label>
                            <motion.button
                              onClick={() => copyToClipboard(record.record_value, `value-${index}`, 'record value')}
                              className="text-xs px-2 py-0.5 bg-gray-700 rounded hover:bg-gray-600"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              Copy
                            </motion.button>
                          </div>
                          <code className="block bg-black/50 p-2 rounded break-all">
                            {record.record_value}
                          </code>
                        </div>
                      </div>

                      <div className="flex gap-6 mt-3 text-sm">
                        {record.priority && (
                          <div>
                            <label className="text-gray-400">Priority:</label>
                            <code className="ml-2">{record.priority}</code>
                          </div>
                        )}
                        <div>
                          <label className="text-gray-400">TTL:</label>
                          <code className="ml-2">{record.ttl} seconds</code>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                <div className="bg-green-900/20 border border-green-600 rounded-lg p-4">
                  <h3 className="font-bold text-green-400 mb-2">📋 DNS Provider Instructions:</h3>
                  <ol className="list-decimal list-inside space-y-1 text-sm">
                    <li>Log into your DNS provider (Cloudflare, GoDaddy, Namecheap, etc.)</li>
                    <li>Navigate to DNS settings for {selectedDomain.domain}</li>
                    <li>Add each record exactly as shown above</li>
                    <li>Wait 10-15 minutes for propagation</li>
                    <li>Click "Verify Domain" to check the records</li>
                  </ol>
                </div>
              </>
            ) : (
              <div className="bg-gray-800 rounded-lg p-8 text-center">
                <p className="text-gray-400 mb-4">Select a domain to view DNS records</p>
                <button
                  onClick={() => setActiveTab('manage')}
                  className="px-4 py-2 bg-blue-600 rounded font-bold hover:bg-blue-700"
                >
                  View Domains
                </button>
              </div>
            )}
          </motion.div>
        )}

        {/* Activity Log Tab */}
        {activeTab === 'activity' && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold">Activity Log</h2>
              <div className="flex gap-2">
                <span className="text-sm text-gray-400">
                  Showing last {activityLog.length} operations
                </span>
                <motion.button
                  onClick={() => setActivityLog([])}
                  className="px-3 py-1 bg-red-600 rounded text-sm hover:bg-red-700"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Clear Log
                </motion.button>
              </div>
            </div>

            {activityLog.length === 0 ? (
              <div className="bg-gray-800 rounded-lg p-8 text-center">
                <p className="text-gray-400">No activity yet. Start managing domains to see logs here.</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                <AnimatePresence>
                  {activityLog.map((entry) => (
                    <motion.div
                      key={entry.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      className={`border rounded-lg p-3 ${getLogTypeColor(entry.type)}`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <span className="font-medium">{entry.action}</span>
                            {entry.domainName && (
                              <span className="text-xs bg-gray-700 px-2 py-0.5 rounded">
                                {entry.domainName}
                              </span>
                            )}
                            {entry.duration !== undefined && (
                              <span className="text-xs bg-gray-700 px-2 py-0.5 rounded">
                                {entry.duration}ms
                              </span>
                            )}
                          </div>
                          <div className="text-xs opacity-75">
                            {formatTimestamp(entry.timestamp)}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <motion.button
                            onClick={() => toggleExpandedDetails(entry.id)}
                            className="text-xs px-2 py-1 bg-gray-700 rounded hover:bg-gray-600"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            {expandedDetails[entry.id] ? 'Hide' : 'Details'}
                          </motion.button>
                          <motion.button
                            onClick={() => copyToClipboard(JSON.stringify(entry, null, 2), `log-${entry.id}`, 'activity log entry')}
                            className="text-xs px-2 py-1 bg-gray-700 rounded hover:bg-gray-600"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            Copy
                          </motion.button>
                        </div>
                      </div>

                      <AnimatePresence>
                        {expandedDetails[entry.id] && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="mt-3 pt-3 border-t border-gray-600"
                          >
                            <pre className="text-xs bg-black/30 p-3 rounded overflow-x-auto">
                              {JSON.stringify(entry.details, null, 2)}
                            </pre>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default DomainManagerEnhanced;